import { motion } from "framer-motion";
import { useEffect, useState } from "react";
import { Resource } from "../types";
import { ExternalLink, Book, Video, FileText, Code } from "lucide-react";
import { useLocale } from "../context/LocaleContext";
import api from "../services/api";

const typeIcons = {
  article: FileText,
  video: Video,
  documentation: Book,
  tool: Code,
};

interface ResourcesPageData {
  title: string;
  subtitle: string;
  description: string;
  noResourcesMessage: string;
  allCategoriesText: string;
}

export function ResourcesPage() {
  const { locale } = useLocale();
  const [resources, setResources] = useState<Resource[]>([]);
  const [pageData, setPageData] = useState<ResourcesPageData>({
    title: "DevOps",
    subtitle: "Resources",
    description: "Curated tools, docs, and guides to master DevOps practices.",
    noResourcesMessage: "No resources available yet.",
    allCategoriesText: "All",
  });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<string>("all");

  useEffect(() => {
    async function fetchData() {
      try {
        // Fetch page content
        const pageResponse = await api.getResourcesPage(locale);
        if (pageResponse?.data?.attributes) {
          setPageData(pageResponse.data.attributes);
        } else if (pageResponse?.data) {
          setPageData(pageResponse.data);
        }

        // Fetch resources
        const resourcesResponse = await api.getResources(locale);
        //console.log("Resources response:", resourcesResponse);
        if (resourcesResponse?.data) {
          const resourcesData = Array.isArray(resourcesResponse.data)
            ? resourcesResponse.data.map((item: any) => ({
                id: item.id,
                title: item.title,
                description: item.description,
                url: item.url,
                type: item.type,
                category: item.category?.name || "Uncategorized",
                created_at: item.createdAt,
              }))
            : [];
          //console.log("Processed resources:", resourcesData);
          setResources(resourcesData);
        }
      } catch (error) {
        console.error("Failed to fetch resources data:", error);
      } finally {
        setLoading(false);
      }
    }

    fetchData();
  }, [locale]);

  const categories = [
    "all",
    ...Array.from(new Set(resources.map((r) => r.category))),
  ];
  const filteredResources =
    filter === "all"
      ? resources
      : resources.filter((r) => r.category === filter);

  return (
    <div className="min-h-screen bg-black py-24 px-4">
      <div className="container mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            {pageData.title}{" "}
            <span className="text-red-600">{pageData.subtitle}</span>
          </h1>
          <p className="text-gray-400 text-lg max-w-2xl mx-auto">
            {pageData.description}
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="flex flex-wrap gap-3 justify-center mb-12"
        >
          {categories.map((category) => (
            <motion.button
              key={category}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setFilter(category)}
              className={`px-6 py-2 rounded-lg font-semibold transition-colors capitalize ${
                filter === category
                  ? "bg-red-600 text-white"
                  : "bg-gray-800 text-gray-300 hover:bg-gray-700"
              }`}
            >
              {category === "all" ? pageData.allCategoriesText : category}
            </motion.button>
          ))}
        </motion.div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-12 h-12 border-4 border-red-600 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : filteredResources.length === 0 ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center py-20"
          >
            <Book className="w-16 h-16 text-gray-600 mx-auto mb-4" />
            <p className="text-gray-400 text-lg">
              {pageData.noResourcesMessage}
            </p>
          </motion.div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredResources.map((resource, idx) => {
              const Icon =
                typeIcons[resource.type as keyof typeof typeIcons] || FileText;

              return (
                <motion.a
                  key={resource.id}
                  href={resource.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.05 }}
                  whileHover={{ y: -5 }}
                  className="bg-gray-900/50 backdrop-blur-sm border border-red-900/20 rounded-xl p-6 hover:border-red-600/50 transition-all group"
                >
                  <div className="flex items-start justify-between mb-4">
                    <Icon className="w-8 h-8 text-red-600" />
                    <ExternalLink className="w-5 h-5 text-gray-500 group-hover:text-red-600 transition-colors" />
                  </div>

                  <h3 className="text-xl font-bold mb-2 group-hover:text-red-600 transition-colors">
                    {resource.title}
                  </h3>

                  <p className="text-gray-400 mb-4 line-clamp-3">
                    {resource.description}
                  </p>

                  <div className="flex items-center gap-2 text-sm">
                    <span className="px-3 py-1 bg-red-900/20 text-red-400 rounded-full capitalize">
                      {resource.type}
                    </span>
                    {resource.category && (
                      <span className="px-3 py-1 bg-gray-800 text-gray-400 rounded-full capitalize">
                        {resource.category}
                      </span>
                    )}
                  </div>
                </motion.a>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
